import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { alpha, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import PrintIcon from "@mui/icons-material/Print";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { orange, red } from "@mui/material/colors";
import { validateArrayIncludes } from "../../../common/validators";
import {
  ORDER_STATUS_DISPATCHABLE,
  OrderTotalRanking,
} from "../../../types/order";
import {
  getChannelLogoPath,
  getIconColor,
  getIconComponent,
} from "../../../utils/merchant";
import {
  addOrderToDispatch,
  removeOrderToDispatch,
} from "../../../store/actions/ordersSlice";
import {
  getStatusIcon,
  getStatusNumberIcon,
} from "../../../common/statusIcons";
import { timeMask } from "../../../common/dateMask";
import {
  getStatusNumberBackgroundColor,
  getStatusNumberIconColor,
  getStatusNumberText,
} from "../../../utils/orders";

const CompactOrderCard = ({
  order,
  onViewDetails,
  onAcceptOrder,
  onPrintOrder,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const ordersToDispatch = useSelector(
    (state) => state.orders.ordersToDispatch
  );

  const [isChecked, setIsChecked] = useState(
    ordersToDispatch.includes(order.order_id)
  );

  useEffect(() => {
    setIsChecked(ordersToDispatch.includes(order.order_id));
  }, [ordersToDispatch, order.order_id]);

  const orderNumber = order.order_index || "0000";
  const orderChannelNumber = order.channel_code || "####";
  const customerName = order.customer_name || "Cliente";
  const rankingByTotal = order.ranking_by_total;
  const orderTotal = order.total || 0;

  const isActuallyLate =
    order.lateStatus && validateArrayIncludes([2, 7], order.status_number);
  const isAlmostLate =
    order.isAlmostLate && validateArrayIncludes([2, 7], order.status_number);

  const isDispatchable =
    order.order_type === "DELIVERY" &&
    validateArrayIncludes(ORDER_STATUS_DISPATCHABLE, order.status_number);

  const isSpecialRanking = rankingByTotal === OrderTotalRanking.SPECIAL;
  const isNewOrder = order.status_number === 0;

  const getMerchantFoodTypeIcon = (foodTypeIcon) => {
    return getIconComponent(foodTypeIcon || "RestaurantIcon");
  };

  const getMerchantIconColor = (foodTypeIcon) => {
    return getIconColor(foodTypeIcon || "RestaurantIcon");
  };

  const getOrderCarColor = (isActuallyLate, isAlmostLate, isSelected) => {
    if (isSelected) {
      return alpha(theme.palette.primary.main, 0.1);
    }

    if (isActuallyLate) {
      return alpha(red[300], 0.2);
    }

    if (isAlmostLate) {
      return alpha(orange[300], 0.2);
    }

    return "#ffffff";
  };

  const getChannelMerchantInitials = (channelMerchant) => {
    if (!channelMerchant) return "N/A";

    return channelMerchant
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .join("")
      .substring(0, 2);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  // Get ranking styling for the total chip
  const getTotalChipStyling = () => {
    const rankingByTotal = order.ranking_by_total;

    if (rankingByTotal === OrderTotalRanking.SPECIAL) {
      return {
        bgcolor: "#FFD700", // Golden background
        color: "#8B4513", // Dark golden text
        fontWeight: 700,
        iconColor: "#DAA520", // Dark golden icon
      };
    }

    if (rankingByTotal === OrderTotalRanking.LOW) {
      return {
        bgcolor: "transparent",
        color: "#E53E3E", // Red text
        fontWeight: 600,
        iconColor: "#E53E3E",
        border: `1px solid ${alpha("#E53E3E", 0.5)}`,
      };
    }

    // Default styling for REGULAR orders
    return {
      bgcolor: isNewOrder
        ? "rgba(255, 255, 255, 0.2)"
        : alpha(theme.palette.success.main, 0.1),
      color: isNewOrder ? "#FFFFFF" : theme.palette.success.main,
      fontWeight: 600,
      iconColor: isNewOrder ? "#FFFFFF" : theme.palette.success.main,
      backdropFilter: isNewOrder ? "blur(10px)" : "none",
      border: isNewOrder ? "1px solid rgba(255, 255, 255, 0.3)" : "none",
    };
  };

  const handleDispatchButtonClick = useCallback(
    (event) => {
      event.stopPropagation();

      if (isChecked) {
        dispatch(removeOrderToDispatch(order.order_id));
      } else {
        dispatch(addOrderToDispatch(order.order_id));
      }
    },
    [dispatch, order.order_id, isChecked]
  );

  const handleViewDetails = useCallback(() => {
    onViewDetails(order);
  }, [order, onViewDetails]);

  const handleAcceptOrder = useCallback(
    (e) => {
      e.stopPropagation();
      onAcceptOrder(order);
    },
    [order, onAcceptOrder]
  );

  const handlePrintOrder = useCallback(
    async (e) => {
      e.stopPropagation();
      await onPrintOrder(order.order_id, e);
    },
    [order.order_id, onPrintOrder]
  );

  const handleKeyDown = useCallback(
    (event) => {
      event.preventDefault();
      handleViewDetails();
    },
    [handleViewDetails]
  );

  const shouldShowAcceptButton = order.status_number === 0;
  const shouldShowPrintButton = validateArrayIncludes(
    [2, 3, 4, 5, 6, 7],
    order.status_number
  );

  const getCardStyles = () => {
    const baseStyles = {
      height: "auto",
      minHeight: 120,
      borderRadius: 2,
      position: "relative",
      backgroundColor: getOrderCarColor(
        isActuallyLate,
        isAlmostLate,
        isChecked
      ),
      border: isChecked
        ? `2px solid ${theme.palette.primary.main}`
        : "1px solid transparent",
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: theme.shadows[4],
      },
      "&:focus-within": {
        transform: "translateY(-2px)",
        boxShadow: theme.shadows[4],
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: "2px",
      },
    };

    if (isNewOrder) {
      return {
        ...baseStyles,
        background: `
          linear-gradient(135deg, 
            rgba(25, 118, 210, 0.95) 0%, 
            rgba(66, 165, 245, 0.95) 50%,
            rgba(25, 118, 210, 0.95) 100%
          )
        `,
        border: `2px solid ${theme.palette.primary.main}`,
        boxShadow: `
          0 0 15px rgba(25, 118, 210, 0.4),
          0 0 30px rgba(25, 118, 210, 0.2)
        `,
        animation: `pulse 2s ease-in-out infinite`,
        "&:hover": {
          ...baseStyles["&:hover"],
          boxShadow: `
            ${theme.shadows[4]},
            0 0 20px rgba(25, 118, 210, 0.6)
          `,
        },
      };
    }

    return baseStyles;
  };

  const createChannelLogo = () => {
    const logoPath = getChannelLogoPath(order.channel);

    if (!logoPath) {
      return null;
    }

    return (
      <Avatar
        src={logoPath}
        sx={{
          width: 16,
          height: 16,
          borderRadius: 100,
        }}
      />
    );
  };

  const createFoodTypeIcon = () => {
    if (!order.food_type_icon) {
      return (
        <Typography
          sx={{
            fontSize: "0.625rem",
            fontWeight: 600,
            color: "#FFFFFF",
          }}
        >
          {getChannelMerchantInitials(order.channel_merchant)}
        </Typography>
      );
    }

    return React.createElement(getMerchantFoodTypeIcon(order.food_type_icon), {
      sx: { fontSize: 12, color: "#FFFFFF" },
    });
  };

  const createStatusIcon = () => {
    if (order.status_number) {
      return getStatusNumberIcon(order.status_number);
    }

    return getStatusIcon(order.status);
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { 
              box-shadow: 
                0 0 15px rgba(25, 118, 210, 0.4),
                0 0 30px rgba(25, 118, 210, 0.2);
            }
            50% { 
              box-shadow: 
                0 0 20px rgba(25, 118, 210, 0.6),
                0 0 40px rgba(25, 118, 210, 0.3);
            }
            100% { 
              box-shadow: 
                0 0 15px rgba(25, 118, 210, 0.4),
                0 0 30px rgba(25, 118, 210, 0.2);
            }
          }
        `}
      </style>
      <Card sx={getCardStyles()}>
        {/* Top Row - Icons and Actions */}
        <Box
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            right: 6,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 3,
          }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            {/* Food Type Icon */}
            <Tooltip title={order.channel_merchant || "Merchant não definido"}>
              <Box
                sx={{
                  bgcolor: alpha(
                    getMerchantIconColor(order.food_type_icon),
                    0.9
                  ),
                  borderRadius: "50%",
                  width: 24,
                  height: 24,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: 1,
                }}
              >
                {createFoodTypeIcon()}
              </Box>
            </Tooltip>

            {/* Special Ranking Crown */}
            {isSpecialRanking && (
              <Tooltip title="Pedido VIP - Alto Valor">
                <Box
                  sx={{
                    bgcolor: alpha("#FFD700", 0.9),
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: 1,
                  }}
                >
                  <WorkspacePremiumIcon
                    sx={{ fontSize: 12, color: "#B8860B" }}
                  />
                </Box>
              </Tooltip>
            )}
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            {/* Action Buttons */}
            {shouldShowAcceptButton && (
              <Tooltip title="Aceitar Pedido">
                <IconButton
                  size="small"
                  onClick={handleAcceptOrder}
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: isNewOrder
                      ? "rgba(76, 175, 80, 0.2)"
                      : alpha(theme.palette.success.main, 0.1),
                    color: isNewOrder ? "#FFFFFF" : theme.palette.success.main,
                    "&:hover": {
                      bgcolor: isNewOrder
                        ? "rgba(76, 175, 80, 0.3)"
                        : alpha(theme.palette.success.main, 0.2),
                    },
                  }}
                >
                  <ThumbUpAltIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            )}

            {shouldShowPrintButton && (
              <Tooltip title="Imprimir Pedido">
                <IconButton
                  size="small"
                  onClick={handlePrintOrder}
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: isNewOrder
                      ? "rgba(25, 118, 210, 0.2)"
                      : alpha(theme.palette.primary.main, 0.1),
                    color: isNewOrder ? "#FFFFFF" : theme.palette.primary.main,
                    "&:hover": {
                      bgcolor: isNewOrder
                        ? "rgba(25, 118, 210, 0.3)"
                        : alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <PrintIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Dispatch Button */}
            {isDispatchable && (
              <Tooltip
                title={
                  isChecked ? "Remover do despacho" : "Adicionar ao despacho"
                }
              >
                <Button
                  variant={isChecked ? "contained" : "outlined"}
                  size="small"
                  onClick={handleDispatchButtonClick}
                  sx={{
                    minWidth: "auto",
                    height: 24,
                    fontSize: "0.625rem",
                    fontWeight: 600,
                    px: 1,
                    py: 0,
                    borderRadius: 1,
                    textTransform: "none",
                    lineHeight: 1,
                    bgcolor: isChecked
                      ? theme.palette.primary.main
                      : "transparent",
                    color: isChecked ? "#FFFFFF" : theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      bgcolor: isChecked
                        ? theme.palette.primary.dark
                        : alpha(theme.palette.primary.main, 0.08),
                      borderColor: isChecked
                        ? theme.palette.primary.dark
                        : theme.palette.primary.main,
                    },
                    "&:active": {
                      bgcolor: isChecked
                        ? theme.palette.primary.dark
                        : alpha(theme.palette.primary.main, 0.12),
                    },
                  }}
                >
                  {isChecked ? "DESPACHANDO" : "DESPACHAR"}
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <CardActionArea
          onClick={handleViewDetails}
          onKeyDown={handleKeyDown}
          role="button"
          aria-label={`Pedido ${orderNumber} - ${customerName}`}
          sx={{
            height: "100%",
            p: 1.5,
            pt: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 }, flex: 1 }}>
            {/* Main Content Row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              {/* Order Number */}
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: isMobile ? "1.75rem" : "2rem",
                  lineHeight: 1,
                  color: isNewOrder ? "#FFFFFF" : theme.palette.text.primary,
                  textShadow: isNewOrder
                    ? "0 1px 2px rgba(0, 0, 0, 0.3)"
                    : "none",
                }}
              >
                {orderNumber}
              </Typography>

              {/* Price Tag */}
              <Box
                sx={{
                  ...getTotalChipStyling(),
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <AttachMoneyIcon
                  sx={{ fontSize: 14, color: getTotalChipStyling().iconColor }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: getTotalChipStyling().fontWeight,
                    fontSize: "0.875rem",
                  }}
                >
                  {formatPrice(orderTotal)}
                </Typography>
              </Box>
            </Box>

            {/* Channel and Customer Info */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Chip
                avatar={createChannelLogo()}
                label={orderChannelNumber}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.75rem",
                  bgcolor: isNewOrder
                    ? "rgba(255, 255, 255, 0.15)"
                    : alpha(theme.palette.grey[100], 0.8),
                  color: isNewOrder ? "#FFFFFF" : theme.palette.text.primary,
                }}
              />

              <Typography
                variant="body2"
                sx={{
                  color: isNewOrder ? "#FFFFFF" : theme.palette.text.secondary,
                  fontWeight: 500,
                  maxWidth: "60%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "right",
                }}
              >
                {customerName}
              </Typography>
            </Box>

            {/* Bottom Row - Delivery Info and Status */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip
                title={`${isDispatchable ? "Entrega" : "Retirada"} - ${timeMask(
                  order.delivery_expected_time
                )}`}
              >
                <Chip
                  icon={
                    isDispatchable ? (
                      <LocalShippingIcon
                        sx={{ fontSize: "0.75rem !important" }}
                      />
                    ) : (
                      <StorefrontIcon sx={{ fontSize: "0.75rem !important" }} />
                    )
                  }
                  color={
                    isActuallyLate
                      ? "error"
                      : isAlmostLate
                      ? "warning"
                      : "default"
                  }
                  label={timeMask(order.delivery_expected_time)}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    height: 20,
                    flex: 1,
                    minWidth: 0,
                  }}
                />
              </Tooltip>
              <Tooltip title={getStatusNumberText(order.status_number)}>
                <Chip
                  icon={createStatusIcon()}
                  label={getStatusNumberText(order.status_number)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    color: "#ffffff",
                    backgroundColor: isNewOrder
                      ? "rgba(255, 255, 255, 0.2)"
                      : getStatusNumberBackgroundColor(order.status_number),
                    fontWeight: 600,
                    backdropFilter: isNewOrder ? "blur(10px)" : "none",
                    border: isNewOrder
                      ? "1px solid rgba(255, 255, 255, 0.3)"
                      : "none",
                    "& .MuiChip-icon": {
                      color: isNewOrder
                        ? "#FFFFFF"
                        : getStatusNumberIconColor(order.status_number),
                    },
                  }}
                />
              </Tooltip>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </>
  );
};

export default CompactOrderCard;
